import {
  type FC,
  type HTMLAttributes,
  type PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
// import BoostIcon from '@assets/img/icons/boost.svg?react';
// import Boost2XIcon from '@assets/img/icons/boost-x2.svg?react';
// import Boost4XIcon from '@assets/img/icons/boost-x4.svg?react';
// import Boost6XIcon from '@assets/img/icons/boost-x6.svg?react';
// import Boost8XIcon from '@assets/img/icons/boost-x8.svg?react';
import fireAnimation from '@assets/lottie/boost-fire.json';
import { useCountDownTimer } from '@common/hooks/count-down-timer';
import cn from 'classnames';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';

import { StringSmall } from '@/components/Typography/Typography';

import { Button } from '../Button/Button';
import { Container } from '../Container/container';

import arrowSvg from '/img/icons/BoostX5X10/arrow.svg';
import boostSvg from '/img/icons/BoostX5X10/boostClear.svg';

interface BoostButton
  extends HTMLAttributes<HTMLButtonElement>,
    PropsWithChildren {
  expiresTime: number;
  value: number;
  duration: number;
  isFreeBoostAvailable: boolean;
}

const BoostButton: FC<BoostButton> = ({
  value,
  expiresTime,
  duration,
  children,
  isFreeBoostAvailable,
  ...props
}) => {
  const { id } = useParams();
  const [totalLength, setTotalLength] = useState(0);
  const svgRef = useRef<SVGRectElement | null>(null);
  const time = useCountDownTimer(expiresTime);

  useEffect(() => {
    const totalLength = svgRef.current?.getTotalLength();
    setTotalLength(totalLength || 0);
    svgRef.current?.animate(
      [{ strokeDashoffset: 0 }, { strokeDashoffset: totalLength }],
      {
        duration: 4000,
        iterations: Infinity,
      },
    );
  }, [id]);

  const width = (totalLength * 20) / 100;

  return (
    <Button
      {...props}
      className={cn(
        '!w-12 !h-12 !rounded-full shrink-0 relative',
        props.className,
      )}
      variant="transparent"
    >
      {children}
      <Container
        className="!rounded-full w-full h-full flex flex-col items-center pt-1"
        variant="circle-button"
      >
        {isFreeBoostAvailable && (
          <>
            <Lottie
              className="absolute top-0 left-0 -translate-y-8 w-12 h-12 -z-[1]"
              animationData={fireAnimation}
            />
            <div className="absolute w-full h-full top-0 left-0 rounded-full bg-main-bg -z-[1]"></div>
          </>
        )}
        {/* {value === 0 && <BoostIcon className="w-10 h-[2.375rem] shrink-0" />}
        {value === 2 && <Boost2XIcon className="w-10 h-[2.375rem] shrink-0" />}
        {value === 4 && <Boost4XIcon className="w-10 h-[2.375rem] shrink-0" />}
        {value === 6 && <Boost6XIcon className="w-10 h-[2.375rem] shrink-0" />}
        {value === 8 && <Boost8XIcon className="w-10 h-[2.375rem] shrink-0" />} */}

        <img src={boostSvg} alt="boost" className="w-10" />
        <motion.img
          src={arrowSvg}
          alt="arrow"
          initial={{ rotate: expiresTime ? 165 : 15 }}
          animate={{ rotate: expiresTime ? 165 : 15 }}
          className="absolute top-[1.4375rem] left-[0.5625rem] origin-right rotate-[15deg] origin-[87%_50%]"
        />
        <StringSmall
          weight="bold"
          className={cn(
            '!text-sm !leading-none',
            value === 0 ? '!text-white' : '!text-[#82e024]',
          )}
        >
          {value === 0 ? 'Boost' : `x${value}`}
        </StringSmall>
      </Container>
      <div
        className={cn(
          !!value && !!expiresTime && !!time ? 'opacity-100' : 'opacity-0',
          'w-[calc(100%+10px)] h-[calc(100%+10px)] absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
        )}
      >
        <svg height="100%" width="100%" xmlns="http://www.w3.org/2000/svg">
          <rect
            key={`boost-animation-${id}`}
            style={{
              strokeDasharray: `${width} ${totalLength - width}`,
            }}
            ref={svgRef}
            rx={'100%'}
            ry={'100%'}
            className="fill-transparent stroke-[2px] stroke-accent"
            height="100%"
            width="100%"
            transform-origin="50% 50%"
            transform="scale(0.9)"
            strokeLinejoin="round"
            fill="transparent"
          />
        </svg>
      </div>
    </Button>
  );
};

export { BoostButton };
